import { getScheduleSuggestions } from "@/api/schedule";
import type { ScheduleStage } from "@/api/schedule/models";
import { UserRoleEnum } from "@/api/user/models";
import { Button } from "@/components/common/Button";
import { SelectScheduleDatePopover } from "@/components/features/schedule/SelectScheduleDatePopover";
import { InputSearchListTalent } from "@/components/talent/InputSearchListTalent";
import { SCHEDULE_CREATION_ALLOWED_ROLES } from "@/constants/userPermissions";
import { useDepartmentLink } from "@/hooks/common/use-department-link";
import { useDepartmentRoute } from "@/hooks/common/use-department-route";
import { usePredictiveSearch } from "@/hooks/common/use-predictive-search";
import { buildRoute, RoutePathEnum } from "@/routes/paths";
import { useUserStore } from "@/stores/use-user-store";
import { formatDateInSaoPauloTimeZone, isoDateToStandardDate } from "@/utils/date";
import { getUserRoleByDepartment } from "@/utils/user-positions";

interface Props {
  onCreateSchedule?: () => void;
  onSelectDate?: (date: string) => void;
  onToggleAvailability?: (isAvailable: boolean) => void;
  selectedDate: string | null;
  stage: ScheduleStage;
}

export const ScheduleActionsHeader = ({
  onCreateSchedule,
  onSelectDate,
  onToggleAvailability,
  selectedDate,
  stage,
}: Props): JSX.Element => {
  const activeUser = useUserStore((state) => state.activeUser);
  const { department } = useDepartmentRoute();
  const buildDepartmentLink = useDepartmentLink();
  const activeRole = getUserRoleByDepartment(activeUser, department);
  const producerPosition = activeUser?.positions.find(
    (position) => position.department === department && position.role === UserRoleEnum.PRODUCER,
  );
  const canCreateSchedule = Boolean(activeRole && SCHEDULE_CREATION_ALLOWED_ROLES.includes(activeRole as UserRoleEnum));
  const formattedSelectedDate = selectedDate ? isoDateToStandardDate(selectedDate) : "";
  const scheduleTitle =
    selectedDate === formatDateInSaoPauloTimeZone() ? `Hoje, ${formattedSelectedDate}` : formattedSelectedDate;
  const { inputProps: predictiveSearchInputProps } = usePredictiveSearch({
    queryKey: ["schedule/suggestions", selectedDate, stage],
    fetch: (searchTerm) =>
      getScheduleSuggestions({
        searchTerm,
        date: selectedDate ?? undefined,
        stage,
      }),
    mapResult: (suggestion) => ({
      id: suggestion.scheduleId,
      imageUrl: suggestion.profilePhotoUrl,
      imageAlt: `Foto de ${suggestion.fullName}`,
      title: suggestion.fullName,
      subtitle: suggestion.talentCode,
      href: buildDepartmentLink(buildRoute(RoutePathEnum.SCHEDULE_TALENT_DATA, { scheduleId: suggestion.scheduleId })),
    }),
  });

  return (
    <>
      <div className="flex items-center">
        <h2 className="font-bold">
          Agenda<span className="font-normal"> | {scheduleTitle}</span>
        </h2>

        <SelectScheduleDatePopover
          key={selectedDate}
          selectedDate={selectedDate ?? ""}
          onSelectDate={(date) => onSelectDate?.(date)}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 md:flex md:items-center md:justify-between mt-6">
        <InputSearchListTalent {...predictiveSearchInputProps} />
        {(canCreateSchedule || producerPosition) && (
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            {producerPosition && (
              <Button
                type="button"
                variant={producerPosition.isAvailable ? "destructive" : "primary"}
                fit
                aria-label={producerPosition.isAvailable ? "Desativar disponibilidade" : "Ativar disponibilidade"}
                onClick={() => onToggleAvailability?.(!producerPosition.isAvailable)}
              >
                {producerPosition.isAvailable ? "Desativar disponibilidade" : "Ativar disponibilidade"}
              </Button>
            )}
            {canCreateSchedule && (
              <Button type="button" variant="secondary" fit aria-label="Relatorio mensal">
                Relatório mensal
              </Button>
            )}
            {canCreateSchedule && (
              <Button
                type="button"
                variant="primary"
                className="!px-3 !text-white"
                fit
                aria-label="Criar novo agendamento"
                onClick={onCreateSchedule}
              >
                Novo agendamento
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
